import { FormGroup } from '@angular/forms';
import type { FormField } from '../../interfaces/form.interface';
import { DropdownData, FilteredValue, InputType } from '../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';
import { DropdownSelectedValueService } from './dropdown-selected-value.service';
import { FormOperationState } from './form-operation-state';

/** Applies dropdown filters and keeps dependent selections coherent. */
export class DropdownFilterExecutor<T> {
    constructor(
        private readonly model: FormGroup,
        private readonly state: FormOperationState,
        private readonly selectedValueService: DropdownSelectedValueService<T>,
    ) {}

    public applyFilter(field: FormField<T>, changedField: string | null): boolean {
        const dropdownData = field.input.data as DropdownData<T>;

        if (typeof field.input?.dropdownConfiguration?.filter === 'function') {
            dropdownData.filtered = [...(dropdownData.values || []).filter(field.input.dropdownConfiguration.filter)];
        }

        return this.applyDependentSelectionState(field, dropdownData, changedField);
    }

    /**
     * Filters dropdown values by the id of another dropdown.
     */
    public getFilteredValues(previousValue: string, field: FormField<T>): (T | null)[] | NonNullable<T> | string {
        const ids = new Set(previousValue.split(';').map((value) => value.trim()));
        const valuePrimaryKey = field.input.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const filteredValues = (field.input.data as DropdownData<T>).filtered
            .filter((value: FilteredValue<T>) => {
                const candidate = value.value as Record<string, string> | null;

                return ids.has(String(candidate?.[valuePrimaryKey] ?? ''));
            })
            .map((value: FilteredValue<T>) => value.value);

        return this.isMultiSelect(field) ? filteredValues : filteredValues[0] ?? '';
    }

    private applyDependentSelectionState(
        field: FormField<T>,
        dropdownData: DropdownData<T>,
        changedField: string | null,
    ): boolean {
        if (!changedField || !field.vinculatedFields?.includes(changedField)) {
            return false;
        }

        const controlName = field.name ?? '';
        const control = this.model.get(controlName);

        if (!control) {
            return false;
        }

        const filteredValues = this.getSelectableFilteredValues(dropdownData);
        const keepValueUntilOptionsLoaded = field.dependencyBehaviour?.keepValueUntilOptionsLoaded === true
            && filteredValues.length === 0;
        const mustClearBecauseDependencyChanged = !keepValueUntilOptionsLoaded
            && this.mustClearBecauseDependencyChanged(field, changedField);
        const nextValue = mustClearBecauseDependencyChanged
            ? this.getClearedControlValue(field)
            : this.getRetainedControlValue(field, control.value, filteredValues);
        const shouldPatchControl = this.shouldPatchControlValue(
            field,
            control.value,
            nextValue,
            mustClearBecauseDependencyChanged,
        );

        this.selectedValueService.applySelected(field, nextValue);

        if (!shouldPatchControl || this.state.isProgrammaticChange) {
            return false;
        }

        this.state.runProgrammaticChange(() => {
            this.model.patchValue({ [controlName]: nextValue }, { emitEvent: false });
            control.markAsUntouched();
            control.markAsPristine();
        });

        return true;
    }

    private mustClearBecauseDependencyChanged(field: FormField<T>, changedField: string | null): boolean {
        if (!changedField || !field.vinculatedFields?.includes(changedField)) {
            return false;
        }

        return field.dependencyBehaviour?.clearOnParentChange ?? true;
    }

    private shouldPatchControlValue(
        field: FormField<T>,
        currentValue: unknown,
        nextValue: unknown,
        forceExactClear: boolean,
    ): boolean {
        if (forceExactClear && currentValue !== nextValue) {
            return true;
        }

        return !this.areControlValuesEquivalent(field, currentValue, nextValue);
    }

    private getClearedControlValue(field: FormField<T>): unknown {
        return this.isMultiSelect(field) ? [] : null;
    }

    private getSelectableFilteredValues(dropdownData: DropdownData<T>): unknown[] {
        const filteredValues = dropdownData.filtered ?? [];
        const selectableValues: unknown[] = [];

        for (const item of filteredValues) {
            if (item.value === null || item.value === undefined) {
                continue;
            }

            selectableValues.push(item.value);
        }

        return selectableValues;
    }

    private getRetainedControlValue(field: FormField<T>, currentValue: unknown, filteredValues: unknown[]): unknown {
        if (this.isMultiSelect(field)) {
            return this.getRetainedMultiValue(field, currentValue, filteredValues);
        }

        return this.getRetainedSingleValue(field, currentValue, filteredValues);
    }

    private getRetainedSingleValue(field: FormField<T>, currentValue: unknown, filteredValues: unknown[]): unknown {
        if (!ReactiveFormValueUtils.hasNonEmptyValue(currentValue)) {
            return null;
        }

        return this.findMatchingFilteredValue(field, currentValue, filteredValues);
    }

    private getRetainedMultiValue(field: FormField<T>, currentValue: unknown, filteredValues: unknown[]): unknown[] {
        const currentValues = Array.isArray(currentValue) ? currentValue : [];
        const retainedValues: unknown[] = [];

        for (const value of currentValues) {
            const retainedValue = this.findMatchingFilteredValue(field, value, filteredValues);

            if (retainedValue !== null) {
                retainedValues.push(retainedValue);
            }
        }

        return retainedValues;
    }

    private findMatchingFilteredValue(field: FormField<T>, selectedValue: unknown, filteredValues: unknown[]): unknown | null {
        for (const filteredValue of filteredValues) {
            if (this.areSingleValuesEquivalent(field, selectedValue, filteredValue)) {
                return filteredValue;
            }
        }

        return null;
    }

    private areControlValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        if (this.isMultiSelect(field)) {
            return this.areMultiValuesEquivalent(field, currentValue, nextValue);
        }

        return this.areSingleValuesEquivalent(field, currentValue, nextValue);
    }

    private areMultiValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        const currentValues = Array.isArray(currentValue) ? currentValue : [];
        const nextValues = Array.isArray(nextValue) ? nextValue : [];

        if (currentValues.length !== nextValues.length) {
            return false;
        }

        return currentValues.every((value, index) => this.areSingleValuesEquivalent(field, value, nextValues[index]));
    }

    private areSingleValuesEquivalent(field: FormField<T>, currentValue: unknown, nextValue: unknown): boolean {
        if (currentValue === nextValue) {
            return true;
        }

        const primaryKey = field.input.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const currentComparableValue = this.extractComparableValue(currentValue, primaryKey);
        const nextComparableValue = this.extractComparableValue(nextValue, primaryKey);

        if (currentComparableValue !== null || nextComparableValue !== null) {
            return currentComparableValue !== null && currentComparableValue === nextComparableValue;
        }

        return ReactiveFormValueUtils.areValuesEquivalent(currentValue, nextValue);
    }

    private extractComparableValue(value: unknown, primaryKey: string): string | null {
        const comparableId = ReactiveFormValueUtils.extractComparableId(value, primaryKey);

        if (comparableId !== null) {
            return String(comparableId);
        }

        if (typeof value === 'boolean') {
            return String(value);
        }

        return null;
    }

    private isMultiSelect(field: FormField<T>): boolean {
        return field.input?.type === InputType.MULTISELECT_SEARCH || field.input?.type === InputType.MULTISELECT;
    }
}
